import { defineSetting } from "idfweb-settings";
import { renderMarkdown } from "idfweb-workbench/markdown";

import type { StartPage } from "./deployment.ts";
import { keptDetail } from "./example-copy.ts";
import { localDirectoriesSupported } from "./local-directory.ts";
import type { ProjectRecord } from "./project-records.ts";
import type { WelcomePage } from "./welcome.ts";

/**
 * The intro: what idfweb is, shown in the welcome window before the project
 * list, and again from the toolbar's help button. "Don't show this again" is
 * a setting, kept in this browser. The options row is where more choices
 * about the page go, such as an analytics opt-out.
 */

const introHidden = defineSetting({
    key: "idfweb.intro.hidden",
    type: "boolean",
    title: "Skip the Introduction",
    description:
        "Open the project list at start instead of the introduction. The introduction still opens while this browser lacks something idfweb needs.",
    group: ["Start"],
    default: false,
    // Stored as "1" before the settings existed.
    legacy: (raw) => (raw === "1" ? true : undefined),
});

/**
 * What the page needs of the browser, and what the user loses without it.
 * Cross-origin isolation is not here: without it main.ts shows nothing else.
 */
const NEEDS: { missing: () => boolean; message: string }[] = [
    {
        missing: () => !exceptionsSupported(),
        message: "This browser cannot run the build tools: its WebAssembly has no exception handling (exnref).",
    },
    {
        missing: () => !growableSharedMemory(),
        message: "This browser cannot build: its SharedArrayBuffer cannot grow.",
    },
    {
        missing: () => !("serial" in navigator),
        message: "This browser has no Web Serial, so it cannot flash or monitor a board.",
    },
    {
        missing: () => !localDirectoriesSupported(),
        message: "This browser cannot open a folder on your computer. The examples still work.",
    },
];

/** The messages for what this browser lacks; empty when it has everything. */
export function missingCapabilities(): string[] {
    return NEEDS.filter((need) => need.missing()).map((need) => need.message);
}

/**
 * Whether the intro opens the welcome window when the page starts. It does
 * while this browser lacks something, even after "Don't show this again".
 */
export function showIntroAtStart(): boolean {
    return !introHidden.get() || missingCapabilities().length > 0;
}

/** A configuration's start example: the copy of it this browser keeps, and how to open either. */
export interface IntroStart {
    kept(): Promise<ProjectRecord | undefined>;
    proceed(record: ProjectRecord): void;
    startOver(): void;
}

/**
 * A deployment's `title` and `welcome` Markdown replace the heading and the
 * text. A configuration with a source links to it below the text. With a
 * start example, the footer opens it in place of the project list.
 */
export function createIntro(
    start: Pick<StartPage, "title" | "welcome" | "configSource"> = {},
    example?: IntroStart,
): WelcomePage {
    return {
        heading: start.title ?? "Welcome to idfweb",
        render: (close, list) => renderIntro(close, list, start.welcome, start.configSource, example),
    };
}

/** The toolbar button that reopens the intro, styled as the device pane's buttons. */
export function createHelpButton(show: () => void): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "idfweb-device-button idfweb-icon-button codicon codicon-question";
    button.title = "About idfweb";
    button.setAttribute("aria-label", "About idfweb");
    button.addEventListener("click", show);
    return button;
}

function renderIntro(
    close: () => void,
    list: () => void,
    welcome: string | undefined,
    source: StartPage["configSource"],
    example: IntroStart | undefined,
): HTMLElement {
    const root = document.createElement("div");
    root.className = "idfweb-intro";
    if (welcome !== undefined) root.append(renderMarkdown(welcome));
    else root.append(...defaultText());
    if (source !== undefined) {
        const note = document.createElement("p");
        note.className = "idfweb-intro-source";
        const link = document.createElement("a");
        link.href = source.url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.textContent = source.label;
        note.append("Source: ", link);
        root.append(note);
    }

    const missing = missingCapabilities();
    if (missing.length > 0) {
        const warning = document.createElement("div");
        warning.className = "idfweb-intro-warning";
        warning.setAttribute("role", "alert");
        const items = document.createElement("ul");
        for (const message of missing) {
            const item = document.createElement("li");
            item.textContent = message;
            items.append(item);
        }
        const advice = document.createElement("p");
        advice.textContent = "Consider using recent versions of Chrome or Edge.";
        warning.append(items, advice);
        root.append(warning);
    }

    const footer = document.createElement("div");
    footer.className = "idfweb-intro-footer";
    if (example !== undefined) {
        footer.append(introButton("Other projects", list), startButtons(close, example));
        root.append(footer);
        return root;
    }
    const options = document.createElement("div");
    options.className = "idfweb-intro-options";
    const label = document.createElement("label");
    const hide = document.createElement("input");
    hide.type = "checkbox";
    hide.checked = introHidden.get();
    hide.addEventListener("change", () => introHidden.set(hide.checked));
    label.append(hide, " Don't show this again");
    options.append(label);
    const next = document.createElement("button");
    next.type = "button";
    next.className = "idfweb-intro-next";
    next.textContent = "Open a project";
    // What the dialog focuses when it opens, rather than its close button.
    next.autofocus = true;
    next.addEventListener("click", list);
    footer.append(options, next);
    root.append(footer);
    return root;
}

/** Start, or Continue and Start over once the kept copy is known, the last one focused. */
function startButtons(close: () => void, example: IntroStart): HTMLElement {
    const actions = document.createElement("div");
    actions.className = "idfweb-intro-actions";
    const then = (run: () => void) => () => {
        close();
        run();
    };
    void example.kept().then((record) => {
        const buttons =
            record === undefined
                ? [introButton("Start", then(example.startOver))]
                : [
                      introButton("Start over", then(example.startOver)),
                      introButton(
                          `Continue ${record.name}`,
                          then(() => example.proceed(record)),
                      ),
                  ];
        if (record !== undefined) buttons[1]!.title = `Open the copy kept in this browser, ${keptDetail(record)}`;
        actions.append(...buttons);
        if (actions.closest("dialog")?.contains(document.activeElement) === true) buttons.at(-1)!.focus();
    });
    return actions;
}

function introButton(text: string, click: () => void): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "idfweb-intro-next";
    button.textContent = text;
    button.addEventListener("click", click);
    return button;
}

/** What idfweb is, where the deployment gives no welcome text of its own. */
function defaultText(): HTMLElement[] {
    const lead = document.createElement("p");
    lead.textContent =
        "idfweb builds ESP-IDF projects in your browser. The compiler, ESP-IDF and its build " +
        "tools run inside the browser using WebAssembly, so nothing has to be installed locally.";
    const abilities = document.createElement("ul");
    for (const text of [
        "Start from an ESP-IDF example, or open a folder on your computer and keep it in sync with the editor.",
        "Edit, build, flash the board and monitor output over USB.",
        "Let an AI agent build and run commands in this tab over MCP.",
    ]) {
        const item = document.createElement("li");
        item.textContent = text;
        abilities.append(item);
    }
    return [lead, abilities];
}

/**
 * Whether WebAssembly validates `try_table`, the exnref form of exception
 * handling the toolchain's modules are built with: a module of one function
 * holding an empty `try_table`.
 */
export function exceptionsSupported(): boolean {
    const module = new Uint8Array([
        0x00, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00, 0x01, 0x04, 0x01, 0x60, 0x00, 0x00, 0x03, 0x02, 0x01, 0x00,
        0x0a, 0x08, 0x01, 0x06, 0x00, 0x1f, 0x40, 0x00, 0x0b, 0x0b,
    ]);
    return typeof WebAssembly === "object" && WebAssembly.validate(module);
}

/** The guest filesystem is a SharedArrayBuffer that grows; see idfweb-vfs. */
function growableSharedMemory(): boolean {
    try {
        return new SharedArrayBuffer(1, { maxByteLength: 2 }).growable === true;
    } catch {
        return false;
    }
}
